import {
  Action,
  EmbedEvent,
  LiveboardEmbed,
  RuntimeFilterOp,
} from "@thoughtspot/visual-embed-sdk"
import PropTypes from "prop-types"
import React, { useEffect, useMemo, useRef, useState } from "react"

import { useThoughtSpotInit } from "./useThoughtSpotInit"

const EMPTY_FILTERS = []

const ThoughtSpotAnalytics = ({
  thoughtspotHost,
  liveboardId,
  scope,
  runtimeFilters = EMPTY_FILTERS,
}) => {
  const [error, setError] = useState(null)
  const [embedHeight, setEmbedHeight] = useState(1200)
  const embedRef = useRef(null)
  const embedInstanceRef = useRef(null)

  useThoughtSpotInit(thoughtspotHost, { scope })

  const formattedFilters = useMemo(
    () =>
      runtimeFilters.map((filter) => ({
        columnName: filter.columnName,
        operator: RuntimeFilterOp[filter.operator],
        values: filter.values,
      })),
    [runtimeFilters]
  )

  useEffect(() => {
    const initializeEmbed = async () => {
      try {
        if (embedRef.current && !embedInstanceRef.current) {
          const embed = new LiveboardEmbed(embedRef.current, {
            frameParams: {
              width: "100%",
              height: "100%",
            },
            fullHeight: true,
            showLiveboardTitle: true,
            isLiveboardCompactHeaderEnabled: true,
            hideIrrelevantChipsInLiveboardTabs: true,
            liveboardId,
            runtimeFilters: formattedFilters,
            hiddenActions: [
              Action.AddToFavorites,
              Action.Share,
              Action.RenameModalTitleDescription,
              Action.ExportTML,
              Action.EditTML,
              Action.UpdateTML,
              Action.Save,
              Action.SaveAsView,
              Action.MakeACopy,
              Action.Edit,
              Action.EditDetails,
              Action.Schedule,
              Action.SchedulesList,
              Action.Subscription,
              Action.ManageMonitor,
              Action.SyncToSlack,
              Action.SyncToTeams,
              Action.RequestAccess,
              Action.RequestVerification,
            ],
          })

          embed.on(EmbedEvent.Error, (e) => {
            console.error("TS EmbedError:", e)
            setError("Failed to load analytics dashboard")
          })

          embed.on(EmbedEvent.Init, () => {
            console.log("TS Init")
          })

          embed.on(EmbedEvent.Load, () => {
            console.log("TS Loaded")
          })

          embed.on(EmbedEvent.EmbedHeight, (data) => {
            console.log("TS Height changed:", data)
            setEmbedHeight(data)
          })

          embedInstanceRef.current = embed
          await embed.render()
        }
      } catch (err) {
        console.error("Failed to initialize ThoughtSpot embed:", err)
        setError("Failed to initialize analytics dashboard")
      }
    }

    initializeEmbed()

    return () => {
      if (embedInstanceRef.current) {
        embedInstanceRef.current.destroy?.()
        embedInstanceRef.current = null
      }
    }
  }, [thoughtspotHost, liveboardId, formattedFilters])

  if (error) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <p style={{ color: "red" }}>{error}</p>
      </div>
    )
  }

  return (
    <div
      ref={embedRef}
      className="thoughtspot-embed-container"
      style={{
        height: `${embedHeight}px`,
        width: "100%",
      }}
    />
  )
}

ThoughtSpotAnalytics.propTypes = {
  thoughtspotHost: PropTypes.string.isRequired,
  liveboardId: PropTypes.string.isRequired,
  scope: PropTypes.string.isRequired,
  runtimeFilters: PropTypes.arrayOf(
    PropTypes.shape({
      columnName: PropTypes.string.isRequired,
      operator: PropTypes.string.isRequired,
      values: PropTypes.array.isRequired,
    })
  ),
}

export default ThoughtSpotAnalytics
